import React, { useState, useEffect } from "react"; // ✅ Import useEffect
import { Modal, Button, Spin } from "antd";
import { svg } from "../../../../Assets/svg";
import { S3FileUploader } from "../../../../Components/S3FileUploader";

interface ChecklistFileUploadModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (fileUrl: string, fileName: string) => void;
  title: string;
}

export const ChecklistFileUploadModal: React.FC<
  ChecklistFileUploadModalProps
> = ({ open, onClose, onSave, title }) => {
  const [loading, setLoading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<{
    url: string;
    name: string;
  } | null>(null);

  // ✅ FIX: Reset state whenever the modal opens
  useEffect(() => {
    if (open) {
      setUploadedFile(null);
      setLoading(false);
    }
  }, [open]);

  // Reset state when modal closes manually
  const handleClose = () => {
    setUploadedFile(null);
    setLoading(false);
    onClose();
  };

  const handleUploadSuccess = (url: string, name: string) => {
    setLoading(false);
    setUploadedFile({ url, name });
  };

  const handleUploadStart = () => {
    setLoading(true);
  };

  const handleConfirmSave = () => {
    if (uploadedFile) {
      onSave(uploadedFile.url, uploadedFile.name);
      // We don't need to clear state here explicitly anymore,
      // because the useEffect will handle it next time it opens.
    }
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      centered
      width={600}
      className="commonModalClass"
      closeIcon={
        <span className="svg-24 cursor-pointer">{svg?.closeIcon || "X"}</span>
      }
    >
      <div className="text-center mb-4 mt-2">
        <h3 className="fs-4 fw-bold mb-0 text-black">{title}</h3>
      </div>

      <div className="mb-4">
        <p className="mb-2 fw-semibold fs-6">Upload File</p>

        {uploadedFile ? (
          // --- VIEW: File Uploaded ---
          <div
            className="d-flex flex-column align-items-center justify-content-center position-relative rounded-3"
            style={{
              border: "2px dashed #d9d9d9",
              backgroundColor: "#fafafa",
              padding: "20px 0",
              marginBottom: "0px",
            }}
          >
            <div className="d-flex flex-column align-items-center">
              {/* Close/Remove Button */}
              <span
                className="position-absolute top-0 end-0 m-2 d-inline-block lh-m cursor-pointer"
                onClick={() => setUploadedFile(null)}
                title="Remove file"
              >
                {svg?.removeIcon || "X"}
              </span>

              {/* File Icon */}
              <span style={{ fontSize: "48px", color: "#ccc" }}>
                {svg?.fileIcon || "📄"}
              </span>

              {/* File Name */}
              <p className="mt-2 mb-0 fw-semibold text-break p-2 text-center">{uploadedFile.name}</p>
            </div>
          </div>
        ) : (
          // --- VIEW: Uploader ---
          <div className="upload-container">
            <S3FileUploader
              folder="quotation_docs"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
              onUploadStart={handleUploadStart}
              onSuccess={handleUploadSuccess}
              onError={() => setLoading(false)}
              className="bg-white rounded-3"
              style={{ padding: "20px", border: "1px dashed #d9d9d9" }}
            >
              {loading ? (
                <div className="py-4 text-center">
                  <Spin size="large" tip="Uploading..." />
                </div>
              ) : (
                <>
                  <p className="ant-upload-drag-icon text-center mb-2 d-flex justify-content-center">
                    <span className="svg-48">{svg?.importIcon || "📄"}</span>
                  </p>
                  <p className="ant-upload-text fw-semibold text-center fs-6 text-secondary">
                    Drag & Drop file /{" "}
                    <span className="text-primary text-decoration-underline cursor-pointer">
                      CLICK HERE
                    </span>
                  </p>
                </>
              )}
            </S3FileUploader>
          </div>
        )}
      </div>

      <div className="d-flex justify-content-center gap-3 mb-2">
        <Button
          size="large"
          className="btn btn-outline-secondary px-5 fw-bold rounded-2 border-secondary"
          onClick={handleClose}
          style={{ height: "48px", minWidth: "140px" }}
        >
          CLOSE
        </Button>
        <Button
          size="large"
          type="primary"
          className="btn btn-dark px-5 fw-bold bg-dark rounded-2 border-0"
          disabled={!uploadedFile || loading}
          onClick={handleConfirmSave}
          style={{ height: "48px", minWidth: "140px" }}
        >
          SAVE
        </Button>
      </div>
    </Modal>
  );
};
